/**
 * ⚠️ Risk Flags Component
 * AI-powered risk analysis display
 */

import React from 'react';

const RiskFlags = ({ analysis, showDetails = true, compact = false }) => {
  if (!analysis) return null;

  const { riskScore, riskLevel, risks, isSafe, requiresCaution, recommendation } = analysis;

  // Color coding
  const getRiskColor = () => {
    if (riskLevel === 'high') return {
      bg: 'bg-red-600/20',
      border: 'border-red-500',
      text: 'text-red-400',
      icon: '🔴',
      label: 'MAGAS KOCKÁZAT'
    };
    if (riskLevel === 'medium') return {
      bg: 'bg-yellow-600/20',
      border: 'border-yellow-500',
      text: 'text-yellow-400',
      icon: '🟡',
      label: 'KÖZEPES KOCKÁZAT'
    };
    return {
      bg: 'bg-green-600/20',
      border: 'border-green-500',
      text: 'text-green-400',
      icon: '🟢',
      label: 'ALACSONY KOCKÁZAT'
    };
  };

  const colors = getRiskColor();

  if (compact) {
    return (
      <div className={`
        inline-flex items-center gap-1.5 px-2 py-1 rounded-full
        ${colors.bg} ${colors.border} border text-xs font-semibold
        ${colors.text}
      `}>
        <span>{colors.icon}</span>
        <span>{colors.label}</span>
        <span className="opacity-75">({riskScore}p)</span>
      </div>
    );
  }

  return (
    <div className={`
      rounded-xl border ${colors.border} ${colors.bg} p-4
      ${riskLevel === 'high' ? 'animate-pulse' : ''}
    `}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`
          w-12 h-12 rounded-full flex items-center justify-center text-2xl
          ${riskLevel === 'high' ? 'bg-red-500' : riskLevel === 'medium' ? 'bg-yellow-500' : 'bg-green-500'}
          text-white
        `}>
          {riskLevel === 'high' ? '⚠️' : riskLevel === 'medium' ? '🤔' : '✅'}
        </div>
        <div>
          <div className={`font-bold text-lg ${colors.text}`}>
            {colors.label}
          </div>
          <div className="text-sm text-gray-400">
            Kockázat pontszám: {riskScore}/100
          </div>
        </div>
        
        {/* Risk meter */}
        <div className="ml-auto">
          <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className={`h-full ${riskLevel === 'high' ? 'bg-red-500' : riskLevel === 'medium' ? 'bg-yellow-500' : 'bg-green-500'}`}
              style={{ width: `${riskScore}%` }}
            />
          </div>
        </div>
      </div>

      {/* Recommendation */}
      <div className={`
        text-sm font-semibold mb-3 p-3 rounded-lg
        ${riskLevel === 'high' ? 'bg-red-500/20 text-red-300' : 
          riskLevel === 'medium' ? 'bg-yellow-500/20 text-yellow-300' : 
          'bg-green-500/20 text-green-300'}
      `}>
        {recommendation}
      </div>

      {/* Risk Details */}
      {showDetails && risks && risks.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">
            Észlelt kockázatok
          </div>
          {risks.map((risk, idx) => (
            <div 
              key={idx}
              className={`
                flex items-start gap-2 p-2 rounded-lg
                ${risk.level === 'high' ? 'bg-red-500/10 border border-red-500/30' : 
                  risk.level === 'medium' ? 'bg-yellow-500/10 border border-yellow-500/30' :
                  'bg-gray-700/50'}
              `}
            >
              <span className="text-lg">{risk.icon}</span>
              <div>
                <div className="text-sm font-semibold text-white">
                  {risk.label}
                </div>
                <div className="text-xs text-gray-400">
                  {risk.reason}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 pt-3 border-t border-white/10 flex gap-2">
        <button className="flex-1 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-colors">
          Részletes elemzés
        </button>
        {riskLevel === 'high' && (
          <button className="px-4 py-2 rounded-lg bg-red-600/50 hover:bg-red-600 text-white text-sm font-semibold transition-colors">
            Jelentés
          </button>
        )}
      </div>
    </div>
  );
};

export default RiskFlags;
